<script setup lang="ts">
import { api } from '@/api';
import {
  EvaluationParameterScopeDtoTypeEnum,
  TaskItemDto,
  TaskTypeDescription,
} from '@/api/generator';
import PtwCancelButton from '@/components/buttons/PtwCancelButton.vue';
import VeeSubmitButton from '@/components/forms/SubmitButton/VeeSubmitButton.vue';
import { useVeeForm } from '@/components/forms/useVeeForm';
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageStatus from '@/components/pages/BasePage/BasePageStatus.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import { formBackendErrorHandler } from '@/errorHandling';
import { routenames } from '@/router/routenames';
import { useNotificationStore } from '@/stores/notificationStore';
import { toCreateOrUpdateTaskItemDto } from '@/views/tasks/mapping.ts';
import {
  validateDoseRates,
  validateEvaluationScopes,
  validateFieldEdges,
  validateParameters,
  taskBackendValidation,
  taskTypeLabel,
} from '@/views/tasks/utils.ts';
import { useTranslation } from 'i18next-vue';
import { NIL } from 'uuid';
import { useRouter } from 'vue-router';
import TaskForm from './components/TaskForm.vue';
import { TaskTypeSelection } from './types';

const props = defineProps<TaskTypeSelection>();

const { t } = useTranslation();

const router = useRouter();

const { category, taskType, subCategory } = props;

const notificationStore = useNotificationStore();

const pageSubTitle = taskTypeLabel(props.taskType);

const initialValues: TaskItemDto = {
  id: NIL,
  name: t('Untitled'),
  description:
    props.taskType === TaskTypeDescription.BeamConstancyCheck
      ? t('TaskItemDescriptionDefaultValue')
      : '',
  category,
  taskType,
  subCategory,
  isAvailable: true,
  taskItemAreas: [
    {
      id: NIL,
      qaEquipmentSetups: [],
      evaluationScopes:
        taskType === 'ManualTask'
          ? [
              {
                id: NIL,
                isEnabled: true,
                condition: 'true',
                evaluationParameters: [],
                $type:
                  EvaluationParameterScopeDtoTypeEnum.PtwPtw100AppZeroApplicationSharedTaskItemComponentDtoEvaluationParametersEvaluationParameterScopeDtoPtwPtw100AppZeroApplicationShared,
              },
            ]
          : [],
    },
  ],
};

const { form } = useVeeForm<TaskItemDto>({
  mode: 'add',
  schemaName: 'TaskItemDto',
  options: {
    initialValues,
    keepValuesOnUnmount: true, // because we render tabs conditionally unmount would clear fields
  },
  customValidateFunctions: [
    taskBackendValidation,
    validateFieldEdges,
    validateDoseRates,
    validateEvaluationScopes,
    validateParameters,
  ],
  confirmationOptions: {
    skipDirtyCheck: 'never', // we decide to never skip "dirty"-dialog, because of initial medical-system selection
  },
});

const { handleSubmit, values } = form;

const submit = handleSubmit(async (dto: TaskItemDto) => {
  const { success, result } = await api.taskItemsApi.createTaskItem(
    {
      createOrUpdateTaskItemDto: toCreateOrUpdateTaskItemDto(dto),
    },
    [formBackendErrorHandler(form)],
  );
  if (success) {
    notificationStore.success(
      t('EntityAdded', {
        name: result.name,
        iuid: result.iuid,
      }),
    );
    await router.push({
      name: routenames.tasks.overview,
    });
  }
});
</script>

<template>
  <form @submit="submit">
    <BasePage>
      <template #title>
        <BasePageTitle :title="values.name" :subtitle="pageSubTitle" />
      </template>

      <template #status>
        <BasePageStatus />
      </template>

      <template #actions>
        <PtwCancelButton :to="{ name: routenames.tasks.overview }" />
        <VeeSubmitButton class="ml-4" :text="$t('SaveAndClose')" />
      </template>
      <TaskForm />
    </BasePage>
  </form>
</template>
